import { EditContent, EditPolygon } from "@/pages/editor";
import { PixelCoordinate } from "@xnav/types/src/common";
import { calcMidPoint, clickingOnCircle, drawCircle } from "./canvasFunctions";

const polygonCircleRadius = 5;
const initalItemColorList = [
	'#FF0000',
	'#00FF00',
	'#0000FF'
]
let initalColorIndex = initalItemColorList.length

export function distanceToLineSegment(
	p: PixelCoordinate,
	a: PixelCoordinate,
	b: PixelCoordinate
): number {
	const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
	if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
	let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
	t = Math.max(0, Math.min(1, t));
	return Math.hypot(p.x - (a.x + t * (b.x - a.x)), p.y - (a.y + t * (b.y - a.y)));
}

export function drawPolygonTitle(ctx: CanvasRenderingContext2D, polygon: EditPolygon) {
	if (!polygon.title) return;
	const { points } = polygon;
	if (!points || points.length === 0) return;
	
	const minX = Math.min(...points.map(p => p.x));
	const maxX = Math.max(...points.map(p => p.x));
	const minY = Math.min(...points.map(p => p.y));
	const maxY = Math.max(...points.map(p => p.y));
	const shapeCenterX = (minX + maxX) / 2;
	const shapeCenterY = (minY + maxY) / 2;

	const position = polygon.titlePosition || 'top';
	const offset = polygon.titleOffset !== undefined ? polygon.titleOffset : (position === 'center' ? 0 : 15);
	const showLine = polygon.showLeaderLine !== false && position !== 'center' && offset > 2;
	
	ctx.save();
	ctx.font = 'bold 11px sans-serif';
	const textMetrics = ctx.measureText(polygon.title);
	const textWidth = textMetrics.width;
	const paddingX = 7;
	const paddingY = 4;
	const boxW = textWidth + paddingX * 2;
	const boxH = 14 + paddingY * 2;

	let pillCenterX = shapeCenterX;
	let pillCenterY = shapeCenterY;
	let anchorX = shapeCenterX;
	let anchorY = shapeCenterY;
	let lineStartX = shapeCenterX;
	let lineStartY = shapeCenterY;
	let lineEndX = shapeCenterX;
	let lineEndY = shapeCenterY;

	if (position === 'top') {
		anchorX = shapeCenterX;
		anchorY = minY;
		pillCenterX = shapeCenterX;
		pillCenterY = minY - offset - boxH / 2;
		lineStartX = shapeCenterX;
		lineStartY = minY;
		lineEndX = shapeCenterX;
		lineEndY = pillCenterY + boxH / 2;
	} else if (position === 'bottom') {
		anchorX = shapeCenterX;
		anchorY = maxY;
		pillCenterX = shapeCenterX;
		pillCenterY = maxY + offset + boxH / 2;
		lineStartX = shapeCenterX;
		lineStartY = maxY;
		lineEndX = shapeCenterX;
		lineEndY = pillCenterY - boxH / 2;
	} else if (position === 'left') {
		anchorX = minX;
		anchorY = shapeCenterY;
		pillCenterX = minX - offset - boxW / 2;
		pillCenterY = shapeCenterY;
		lineStartX = minX;
		lineStartY = shapeCenterY;
		lineEndX = pillCenterX + boxW / 2;
		lineEndY = shapeCenterY;
	} else if (position === 'right') {
		anchorX = maxX;
		anchorY = shapeCenterY;
		pillCenterX = maxX + offset + boxW / 2;
		pillCenterY = shapeCenterY;
		lineStartX = maxX;
		lineStartY = shapeCenterY;
		lineEndX = pillCenterX - boxW / 2;
		lineEndY = shapeCenterY;
	} else {
		// 'center'
		pillCenterX = shapeCenterX;
		pillCenterY = shapeCenterY;
	}

	const boxX = pillCenterX - boxW / 2;
	const boxY = pillCenterY - boxH / 2;

	// Draw Leader Line if enabled and floating outside shape
	if (showLine) {
		ctx.beginPath();
		ctx.moveTo(lineStartX, lineStartY);
		ctx.lineTo(lineEndX, lineEndY);
		ctx.strokeStyle = polygon.color || '#3182ce';
		ctx.lineWidth = 1.5;
		ctx.setLineDash([3, 3]);
		ctx.stroke();

		// Small pin-point dot at anchor point
		ctx.beginPath();
		ctx.arc(anchorX, anchorY, 2.5, 0, 2 * Math.PI);
		ctx.fillStyle = polygon.color || '#3182ce';
		ctx.fill();
	}

	// Draw pill background
	ctx.setLineDash([]);
	ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
	ctx.strokeStyle = polygon.color || '#3182ce';
	ctx.lineWidth = 1;
	ctx.beginPath();
	if (ctx.roundRect) {
		ctx.roundRect(boxX, boxY, boxW, boxH, 6);
	} else {
		ctx.rect(boxX, boxY, boxW, boxH);
	}
	ctx.fill();
	ctx.stroke();

	// Draw title text
	ctx.fillStyle = '#ffffff';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.fillText(polygon.title, pillCenterX, pillCenterY);
	ctx.restore();
}

function drawTextShape(ctx: CanvasRenderingContext2D, polygon: EditPolygon, isEditable: boolean = false, isHidden: boolean = false) {
	const { points, color } = polygon;
	const minX = Math.min(...points.map(p => p.x));
	const maxX = Math.max(...points.map(p => p.x));
	const minY = Math.min(...points.map(p => p.y));
	const maxY = Math.max(...points.map(p => p.y));
	const cX = (minX + maxX) / 2;
	const cY = (minY + maxY) / 2;
	const fSize = polygon.fontSize || 18;
	const fontFam = polygon.fontFamily || 'Inter, sans-serif';
	const fillColor = isHidden ? '#77777710' : (polygon.color + '55');
	const strokeColor = isHidden ? '#777777' : color;
	const borderSt = polygon.borderStyle || 'solid';
	const effect = polygon.textEffect || 'none';

	ctx.save();
	ctx.font = `bold ${fSize}px ${fontFam}`;
	const textStr = polygon.title || 'Text Tag';
	const metrics = ctx.measureText(textStr);
	const textW = metrics.width;
	const boxW = Math.max(textW + 20, maxX - minX);
	const boxH = Math.max(fSize + 14, maxY - minY);
	const boxX = cX - boxW / 2;
	const boxY = cY - boxH / 2;

	// Render Border Box
	if (borderSt !== 'none') {
		ctx.beginPath();
		if (borderSt === 'pill') {
			if (ctx.roundRect) ctx.roundRect(boxX, boxY, boxW, boxH, boxH / 2);
			else ctx.rect(boxX, boxY, boxW, boxH);
		} else {
			if (ctx.roundRect) ctx.roundRect(boxX, boxY, boxW, boxH, 6);
			else ctx.rect(boxX, boxY, boxW, boxH);
		}

		if (!polygon.noFill) {
			ctx.fillStyle = fillColor;
			ctx.fill();
		}

		if (borderSt === 'dashed') {
			ctx.setLineDash([4, 4]);
		} else {
			ctx.setLineDash([]);
		}
		ctx.strokeStyle = strokeColor;
		ctx.lineWidth = polygon.thickness || 2;
		ctx.stroke();
	}

	// Apply Text Effects (Shadow / Glow)
	ctx.setLineDash([]);
	if (effect === 'shadow') {
		ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
		ctx.shadowBlur = 6;
		ctx.shadowOffsetX = 2;
		ctx.shadowOffsetY = 2;
	} else if (effect === 'glow') {
		ctx.shadowColor = strokeColor;
		ctx.shadowBlur = 12;
		ctx.shadowOffsetX = 0;
		ctx.shadowOffsetY = 0;
	}

	ctx.fillStyle = isHidden ? '#777777' : color;
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.fillText(textStr, cX, cY);

	// If Editable mode: draw resize/rotate bounding box handles
	if (isEditable) {
		ctx.shadowColor = 'transparent';
		ctx.shadowBlur = 0;
		ctx.shadowOffsetX = 0;
		ctx.shadowOffsetY = 0;

		let renderMinX = boxX;
		let renderMaxX = boxX + boxW;
		let renderMinY = boxY;
		let renderMaxY = boxY + boxH;
		
		ctx.strokeStyle = '#3182ce'; // Chakra blue.500
		ctx.lineWidth = 1;
		ctx.setLineDash([4, 4]);
		ctx.strokeRect(renderMinX, renderMinY, renderMaxX - renderMinX, renderMaxY - renderMinY);
		
		const renderCenterX = (renderMinX + renderMaxX) / 2;
		
		// Draw line to rotation handle
		ctx.beginPath();
		ctx.moveTo(renderCenterX, renderMinY);
		ctx.lineTo(renderCenterX, renderMinY - 20);
		ctx.stroke();
		
		// Draw scale/resize handle
		ctx.setLineDash([]);
		ctx.fillStyle = '#3182ce';
		ctx.strokeStyle = '#ffffff';
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.rect(renderMaxX - 4, renderMaxY - 4, 8, 8);
		ctx.fill();
		ctx.stroke();
		
		// Draw rotation handle circle
		ctx.fillStyle = '#48bb78'; // Green.400
		ctx.beginPath();
		ctx.arc(renderCenterX, renderMinY - 20, 5, 0, 2 * Math.PI);
		ctx.fill();
		ctx.stroke();
	}

	ctx.restore();
}

export function getCircleEllipseParams(polygon: EditPolygon): { cX: number, cY: number, rx: number, ry: number, rotRad: number, isCircleType: boolean } {
	const points = polygon.points || [];
	const cX = points.length > 0 ? points.reduce((sum, p) => sum + p.x, 0) / points.length : 0;
	const cY = points.length > 0 ? points.reduce((sum, p) => sum + p.y, 0) / points.length : 0;
	const rotDeg = polygon.rotation || 0;
	const rotRad = (rotDeg * Math.PI) / 180;
	const cos = Math.cos(-rotRad);
	const sin = Math.sin(-rotRad);

	let maxU = 0;
	let maxV = 0;
	for (const p of points) {
		const dx = p.x - cX;
		const dy = p.y - cY;
		const u = Math.abs(dx * cos - dy * sin);
		const v = Math.abs(dx * sin + dy * cos);
		if (u > maxU) maxU = u;
		if (v > maxV) maxV = v;
	}

	const isCircleType = polygon.keepAspectRatio === true || Math.abs(maxU - maxV) < 2;
	const rx = Math.max(5, maxU || 40);
	const ry = Math.max(5, isCircleType ? rx : (maxV || 30));

	return { cX, cY, rx, ry, rotRad, isCircleType };
}

export function drawCircleOrEllipse(ctx: CanvasRenderingContext2D, polygon: EditPolygon) {
	const { cX, cY, rx, ry, rotRad, isCircleType } = getCircleEllipseParams(polygon);
	ctx.beginPath();
	if (isCircleType) {
		ctx.arc(cX, cY, rx, 0, 2 * Math.PI);
	} else {
		ctx.ellipse(cX, cY, rx, ry, rotRad, 0, 2 * Math.PI);
	}
}

export function generateCircleEllipsePoints(cX: number, cY: number, rx: number, ry: number, rotDeg: number = 0): PixelCoordinate[] {
	const rotRad = (rotDeg * Math.PI) / 180;
	const cos = Math.cos(rotRad);
	const sin = Math.sin(rotRad);
	const points: PixelCoordinate[] = [];
	const numPoints = 36;
	for (let i = 0; i < numPoints; i++) {
		const angle = (i * 2 * Math.PI) / numPoints;
		const u = rx * Math.cos(angle);
		const v = ry * Math.sin(angle);
		points.push({
			x: Math.round(cX + u * cos - v * sin),
			y: Math.round(cY + u * sin + v * cos)
		});
	}
	return points;
}

export function drawPolygon(ctx: CanvasRenderingContext2D, polygon: EditPolygon) {
	const { points, color } = polygon;
	if (!points || points.length === 0) return;
	const fillColor = polygon.color + '80'; // A little bit transparent

	if (polygon.isText) {
		drawTextShape(ctx, polygon, false, false);
		return;
	}

	if (polygon.isCircle) {
		drawCircleOrEllipse(ctx, polygon);
		if (!polygon.noFill) {
			ctx.fillStyle = fillColor;
			ctx.fill();
		}
		ctx.strokeStyle = color;
		ctx.lineWidth = polygon.thickness || 2;
		ctx.stroke();
		drawPolygonTitle(ctx, polygon);
		return;
	}

	ctx.beginPath();
	ctx.moveTo(points[0].x, points[0].y);
	for (let i = 1; i < points.length; i++) {
		ctx.lineTo(points[i].x, points[i].y);
	}
	if (points.length > 2) {
		ctx.closePath();
	}
	if (!polygon.noFill && points.length > 2) {
		ctx.fillStyle = fillColor;
		ctx.fill();
	}
	ctx.strokeStyle = color;
	ctx.lineWidth = polygon.thickness || 2;
	ctx.stroke();
	
	drawPolygonTitle(ctx, polygon);
}

export function drawHiddenPolygon(ctx: CanvasRenderingContext2D, polygon: EditPolygon) {
	const { points } = polygon;
	if (!points || points.length === 0) return;
	const fillColor = '#77777710'; // A little bit transparent
	const color = '#777777';

	if (polygon.isText) {
		drawTextShape(ctx, polygon, false, true);
		return;
	}

	if (polygon.isCircle) {
		drawCircleOrEllipse(ctx, polygon);
		if (!polygon.noFill) {
			ctx.fillStyle = fillColor;
			ctx.fill();
		}
		ctx.strokeStyle = color;
		ctx.lineWidth = polygon.thickness || 2;
		ctx.stroke();
		return;
	}

	ctx.beginPath();
	ctx.moveTo(points[0].x, points[0].y);
	for (let i = 1; i < points.length; i++) {
		ctx.lineTo(points[i].x, points[i].y);
	}
	if (points.length > 2) {
		ctx.closePath();
	}
	if (!polygon.noFill && points.length > 2) {
		ctx.fillStyle = fillColor;
		ctx.fill();
	}
	ctx.strokeStyle = color;
	ctx.lineWidth = polygon.thickness || 2;
	ctx.stroke();
}

export function drawEditablePolygon(ctx: CanvasRenderingContext2D, polygon: EditPolygon) {
	const { points, midPoints, color } = polygon;
	if (!points || points.length === 0) return;
	const fillColor = polygon.color + '55'; // A little bit transparent
	const halfwayPointFillColor = polygon.color + '00'; // More transparent

	if (polygon.isText) {
		drawTextShape(ctx, polygon, true, false);
		return;
	}

	if (polygon.isCircle) {
		drawCircleOrEllipse(ctx, polygon);
		if (!polygon.noFill) {
			ctx.fillStyle = fillColor;
			ctx.fill();
		}
		ctx.strokeStyle = color;
		ctx.lineWidth = polygon.thickness || 2;
		ctx.stroke();

		// Draw bounding box (with safety minimum sizes to prevent collapse on lines)
		const minX = Math.min(...points.map(p => p.x));
		const maxX = Math.max(...points.map(p => p.x));
		const minY = Math.min(...points.map(p => p.y));
		const maxY = Math.max(...points.map(p => p.y));
		
		let renderMinX = minX;
		let renderMaxX = maxX;
		let renderMinY = minY;
		let renderMaxY = maxY;
		
		if (renderMaxX - renderMinX < 10) {
			renderMinX -= 5;
			renderMaxX += 5;
		}
		if (renderMaxY - renderMinY < 10) {
			renderMinY -= 5;
			renderMaxY += 5;
		}
		
		ctx.save();
		ctx.strokeStyle = '#3182ce'; // Chakra blue.500
		ctx.lineWidth = 1;
		ctx.setLineDash([4, 4]);
		ctx.strokeRect(renderMinX, renderMinY, renderMaxX - renderMinX, renderMaxY - renderMinY);
		
		const renderCenterX = (renderMinX + renderMaxX) / 2;
		
		// Draw line to rotation handle
		ctx.beginPath();
		ctx.moveTo(renderCenterX, renderMinY);
		ctx.lineTo(renderCenterX, renderMinY - 20);
		ctx.stroke();
		
		// Draw scale/resize handle at bottom-right corner of render bounding box
		ctx.setLineDash([]);
		ctx.fillStyle = '#3182ce';
		ctx.strokeStyle = '#ffffff';
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.rect(renderMaxX - 4, renderMaxY - 4, 8, 8);
		ctx.fill();
		ctx.stroke();
		
		// Draw rotation handle circle
		ctx.fillStyle = '#48bb78'; // Green.400
		ctx.beginPath();
		ctx.arc(renderCenterX, renderMinY - 20, 5, 0, 2 * Math.PI);
		ctx.fill();
		ctx.stroke();
		ctx.restore();
		drawPolygonTitle(ctx, polygon);
		return;
	}

	ctx.beginPath();
	ctx.moveTo(points[0].x, points[0].y);
	for (let i = 1; i < points.length; i++) {
		ctx.lineTo(points[i].x, points[i].y);
	}
	if (points.length > 2) {
		ctx.closePath();
	}
	if (!polygon.noFill && points.length > 2) {
		ctx.fillStyle = fillColor;
		ctx.fill();
	}
	ctx.strokeStyle = color;
	ctx.lineWidth = polygon.thickness || 2;
	ctx.stroke();
	if (!polygon.isPreMade) {
		for (let i = 0; i < points.length; i++) {
			drawCircle(ctx, points[i].x, points[i].y, 5, fillColor, color);
		}
		if (midPoints) {
			for (let i = 0; i < midPoints.length; i++) {
				drawCircle(ctx, midPoints[i].x, midPoints[i].y, 5, halfwayPointFillColor, color);
			}
		}
	}
	
	// Draw bounding box (with safety minimum sizes to prevent collapse on lines)
	const minX = Math.min(...points.map(p => p.x));
	const maxX = Math.max(...points.map(p => p.x));
	const minY = Math.min(...points.map(p => p.y));
	const maxY = Math.max(...points.map(p => p.y));
	
	let renderMinX = minX;
	let renderMaxX = maxX;
	let renderMinY = minY;
	let renderMaxY = maxY;
	
	if (renderMaxX - renderMinX < 10) {
		renderMinX -= 5;
		renderMaxX += 5;
	}
	if (renderMaxY - renderMinY < 10) {
		renderMinY -= 5;
		renderMaxY += 5;
	}
	
	ctx.save();
	ctx.strokeStyle = '#3182ce'; // Chakra blue.500
	ctx.lineWidth = 1;
	ctx.setLineDash([4, 4]);
	ctx.strokeRect(renderMinX, renderMinY, renderMaxX - renderMinX, renderMaxY - renderMinY);
	
	const renderCenterX = (renderMinX + renderMaxX) / 2;
	
	// Draw line to rotation handle
	ctx.beginPath();
	ctx.moveTo(renderCenterX, renderMinY);
	ctx.lineTo(renderCenterX, renderMinY - 20);
	ctx.stroke();
	
	// Draw scale/resize handle at bottom-right corner of render bounding box
	ctx.setLineDash([]);
	ctx.fillStyle = '#3182ce';
	ctx.strokeStyle = '#ffffff';
	ctx.lineWidth = 1.5;
	ctx.beginPath();
	ctx.rect(renderMaxX - 4, renderMaxY - 4, 8, 8);
	ctx.fill();
	ctx.stroke();
	
	// Draw rotation handle circle
	ctx.fillStyle = '#48bb78'; // Green.400
	ctx.beginPath();
	ctx.arc(renderCenterX, renderMinY - 20, 5, 0, 2 * Math.PI);
	ctx.fill();
	ctx.stroke();
	ctx.restore();
	drawPolygonTitle(ctx, polygon);
}

export function clickingOnPolygon(click: PixelCoordinate, polygon: EditPolygon): boolean {
	const { points } = polygon;
	if (!points || points.length === 0) return false;

	// Handle 2-point line tool hit testing
	if (points.length === 2) {
		const hitThreshold = Math.max(12, ((polygon.thickness || 2) / 2) + 8);
		return distanceToLineSegment(click, points[0], points[1]) <= hitThreshold;
	}

	// Handle bounding box proximity for horizontal or vertical line shapes
	const minX = Math.min(...points.map(p => p.x));
	const maxX = Math.max(...points.map(p => p.x));
	const minY = Math.min(...points.map(p => p.y));
	const maxY = Math.max(...points.map(p => p.y));
	
	if (maxX - minX < 10 || maxY - minY < 10) {
		const padding = Math.max(12, ((polygon.thickness || 2) / 2) + 6);
		if (click.x >= minX - padding && click.x <= maxX + padding && click.y >= minY - padding && click.y <= maxY + padding) {
			return true;
		}
	}

	let inside = false;
	for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
		if (((points[i].y > click.y) !== (points[j].y > click.y)) &&
			(click.x < (points[j].x - points[i].x) * (click.y - points[i].y) / (points[j].y - points[i].y) + points[i].x)) {
			inside = !inside;
		}
	}
	return inside;
}

export function findIndexOfClickedPoint(click: PixelCoordinate, points: PixelCoordinate[]): number {
	for (let i = 0; i < points.length; i++) {
		if (clickingOnCircle(click, points[i], polygonCircleRadius)) {
			return i;
		}
	}
	return -1;
}

export function addPointToPolygon(polygon: EditPolygon, click: PixelCoordinate, insertionIndex?: number): EditPolygon {
	if (insertionIndex === undefined) {
		const newPolygon = polygon
		newPolygon.points.push(click)
		newPolygon.midPoints[newPolygon.midPoints.length - 1] = calcMidPoint(newPolygon.points[newPolygon.points.length - 2], click)
		newPolygon.midPoints.push(calcMidPoint(click, newPolygon.points[0]))
		return newPolygon
	} else {
		const newPolygon = polygon
		newPolygon.points.splice(insertionIndex + 1, 0, click)
		newPolygon.midPoints.splice(insertionIndex, 0, calcMidPoint(newPolygon.points[insertionIndex], click))
		newPolygon.midPoints[insertionIndex + 1] = calcMidPoint(click, newPolygon.points[(insertionIndex + 2) % newPolygon.points.length])
		return newPolygon
	}
}

import { MarkupTool } from "@/components/videoMarkupEditor";

export function handlePotentialPolygonClicks(
	click: PixelCoordinate,
	time: number,
	content: EditContent,
	setContent: (content: EditContent) => void,
	tool?: MarkupTool
): boolean {
	const { polygons } = content
	for (let i = 0; i < polygons.length; i++) {
		const polygon = polygons[i];
		if (time < polygon.start || time > polygon.end) continue

		// Check if user clicked the scale or rotation handle first (only for currently selected polygon)
		if (content.selectedMarkup && content.selectedMarkup.type === 'polygon' && content.selectedMarkup.index === i) {
			const { points } = polygon;
			const maxX = Math.max(...points.map(p => p.x));
			const maxY = Math.max(...points.map(p => p.y));
			const minX = Math.min(...points.map(p => p.x));
			const minY = Math.min(...points.map(p => p.y));
			
			const renderCenterX = (minX + maxX) / 2;
			// Check rotation handle (at top center - 20px)
			if (clickingOnCircle(click, { x: renderCenterX, y: minY - 20 }, 10)) {
				setContent({
					...content,
					selectedMarkup: {
						index: i,
						subIndex: -3, // -3 signifies the rotation handle
						type: 'polygon',
					}
				});
				return true;
			}
			
			let clickTargetX = maxX;
			let clickTargetY = maxY;
			if (maxX - minX < 10) {
				clickTargetX = maxX + 5;
			}
			if (maxY - minY < 10) {
				clickTargetY = maxY + 5;
			}
			if (clickingOnCircle(click, { x: clickTargetX, y: clickTargetY }, 10)) {
				setContent({
					...content,
					selectedMarkup: {
						index: i,
						subIndex: -2, // -2 signifies the scale/resize handle
						type: 'polygon',
					}
				});
				return true;
			}
		}

		if (!polygon.isPreMade) {
			const cornerIndex = findIndexOfClickedPoint(click, polygon.points);
			if (cornerIndex !== -1) {
				setContent({
					...content,
					selectedMarkup: {
						index: i,
						subIndex: cornerIndex,
						type: 'polygon',
					}
				})
				return true;
			}
			const midPointIndex = findIndexOfClickedPoint(click, polygon.midPoints);
			if (midPointIndex !== -1) {
				setContent({
					...content,
					selectedMarkup: {
						index: i,
						subIndex: midPointIndex + 1,
						type: 'polygon',
					},
					polygons: polygons.map((polygon: EditPolygon, index) => index === i ? addPointToPolygon(polygon, click, midPointIndex) : polygon)
				})
				return true;
			}
		}
		if (tool !== 'polygon' && clickingOnPolygon(click, polygon)) {
			setContent({
				...content,
				selectedMarkup: {
					index: i,
					subIndex: -1,
					type: 'polygon',
				}
			})
			return true;
		}
	}
	return false;
}

export function handlePolygonPointDrag(
	click: PixelCoordinate,
	content: EditContent,
	setContent: (content: EditContent) => void,
) {
	const { polygons } = content
	const selectedMarkup = content.selectedMarkup!
	const newPolygon = polygons[selectedMarkup.index]
	newPolygon.points[selectedMarkup.subIndex] = click
	const firstMidpointIndex = (selectedMarkup.subIndex === 0 ? newPolygon.midPoints.length : selectedMarkup.subIndex) - 1
	newPolygon.midPoints[firstMidpointIndex] = calcMidPoint(newPolygon.points[firstMidpointIndex], click)
	newPolygon.midPoints[selectedMarkup.subIndex] = calcMidPoint(click, newPolygon.points[(selectedMarkup.subIndex + 1) % newPolygon.points.length])
	setContent({
		...content,
		polygons: polygons.map((polygon, index) => index === selectedMarkup.index ? newPolygon : polygon as EditPolygon)
	})
}

export function handlePolygonDrag(click: PixelCoordinate, lastMousePosition: PixelCoordinate, content: EditContent, setContent: (content: EditContent) => void) {
	const xDiff = click.x - lastMousePosition.x
	const yDiff = click.y - lastMousePosition.y
	const newPolygon = content.polygons[content.selectedMarkup!.index]
	newPolygon.points = newPolygon.points.map(point => {
		return { x: point.x + xDiff, y: point.y + yDiff }
	})
	newPolygon.midPoints = newPolygon.midPoints.map(midPoint => {
		return { x: midPoint.x + xDiff, y: midPoint.y + yDiff }
	})
	const newPolygons = content.polygons
	newPolygons[content.selectedMarkup!.index] = newPolygon
	setContent({
		...content,
		polygons: newPolygons
	})
}

export function createNewPolygon(click: PixelCoordinate, time: number): EditPolygon {
	initalColorIndex = (initalColorIndex + 1) % initalItemColorList.length
	return {
		title: '',
		description: '',
		points: [click],
		color: initalItemColorList[initalColorIndex],
		midPoints: [click],
		hiddenFrames: [],
		start: time,
		end: time + 1,
		audios: []
	}
}

export function createPreMadeShape(
	type: 'triangle' | 'rectangle' | 'pentagon' | 'arrow' | 'line' | 'circle' | 'ellipse' | 'text',
	click: PixelCoordinate,
	time: number
): EditPolygon {
	initalColorIndex = (initalColorIndex + 1) % initalItemColorList.length;
	const color = initalItemColorList[initalColorIndex];
	
	let points: PixelCoordinate[] = [];
	let title = '';
	let noFill = false;
	let isText = false;
	let fontSize = 18;
	
	if (type === 'text') {
		isText = true;
		title = 'Text Tag';
		fontSize = 18;
		points = [
			{ x: click.x - 45, y: click.y - 15 },
			{ x: click.x + 45, y: click.y - 15 },
			{ x: click.x + 45, y: click.y + 15 },
			{ x: click.x - 45, y: click.y + 15 }
		];
	} else if (type === 'triangle') {
		points = [
			{ x: click.x, y: click.y - 40 },
			{ x: click.x - 45, y: click.y + 35 },
			{ x: click.x + 45, y: click.y + 35 }
		];
	} else if (type === 'rectangle') {
		points = [
			{ x: click.x - 50, y: click.y - 40 },
			{ x: click.x + 50, y: click.y - 40 },
			{ x: click.x + 50, y: click.y + 40 },
			{ x: click.x - 50, y: click.y + 40 }
		];
	} else if (type === 'pentagon') {
		points = [
			{ x: click.x, y: click.y - 45 },
			{ x: Math.round(click.x + 43), y: Math.round(click.y - 14) },
			{ x: Math.round(click.x + 26), y: Math.round(click.y + 36) },
			{ x: Math.round(click.x - 26), y: Math.round(click.y + 36) },
			{ x: Math.round(click.x - 43), y: Math.round(click.y - 14) }
		];
	} else if (type === 'arrow') {
		points = [
			{ x: click.x - 45, y: click.y - 15 },
			{ x: click.x + 5, y: click.y - 15 },
			{ x: click.x + 5, y: click.y - 35 },
			{ x: click.x + 45, y: click.y },
			{ x: click.x + 5, y: click.y + 35 },
			{ x: click.x + 5, y: click.y + 15 },
			{ x: click.x - 45, y: click.y + 15 }
		];
	} else if (type === 'line') {
		noFill = true;
		points = [
			{ x: click.x - 50, y: click.y },
			{ x: click.x + 50, y: click.y }
		];
	} else if (type === 'circle') {
		const r = 40;
		for (let i = 0; i < 36; i++) {
			const angle = (i * 10 * Math.PI) / 180;
			points.push({
				x: Math.round(click.x + r * Math.cos(angle)),
				y: Math.round(click.y + r * Math.sin(angle))
			});
		}
	} else if (type === 'circle') {
		points = generateCircleEllipsePoints(click.x, click.y, 45, 45, 0);
	} else if (type === 'ellipse') {
		points = generateCircleEllipsePoints(click.x, click.y, 65, 40, 0);
	}
	
	const shape = generateMidpoints({
		title,
		description: '',
		points,
		color,
		midPoints: [],
		hiddenFrames: [],
		start: time,
		end: time + 1,
		audios: [],
		isPreMade: true,
		thickness: type === 'line' || type === 'circle' || type === 'ellipse' ? 3 : 2,
		isCircle: type === 'circle' || type === 'ellipse',
		keepAspectRatio: type === 'circle',
		isText,
		fontSize
	} as EditPolygon);
	
	if (noFill) {
		shape.noFill = true;
	}
	return shape;
}

export function handlePolygonScale(
	click: PixelCoordinate,
	lastMousePosition: PixelCoordinate,
	content: EditContent,
	setContent: (content: EditContent) => void,
	shiftKey: boolean = false
) {
	const { polygons } = content;
	const selectedMarkup = content.selectedMarkup!;
	const polygon = polygons[selectedMarkup.index];
	const { points } = polygon;
	if (!points || points.length === 0) return;

	const shouldKeepAspect = shiftKey || polygon.keepAspectRatio === true;
	const rotDeg = polygon.rotation || 0;
	const rotRad = (rotDeg * Math.PI) / 180;
	const cX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
	const cY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

	if (polygon.isCircle) {
		const { rx, ry, isCircleType } = getCircleEllipseParams(polygon);
		const lastDist = Math.hypot(lastMousePosition.x - cX, lastMousePosition.y - cY);
		const currDist = Math.hypot(click.x - cX, click.y - cY);
		const scaleFactor = lastDist > 1 ? currDist / lastDist : 1;
		const clampedScale = Math.min(Math.max(scaleFactor, 0.7), 1.4);

		const newRx = Math.max(8, Math.round(rx * clampedScale));
		const newRy = isCircleType ? newRx : Math.max(8, Math.round(ry * clampedScale));
		const newPoints = generateCircleEllipsePoints(cX, cY, newRx, newRy, rotDeg);

		const newPolygon = generateMidpoints({
			...polygon,
			points: newPoints
		} as EditPolygon);

		setContent({
			...content,
			polygons: polygons.map((poly, index) => index === selectedMarkup.index ? newPolygon : poly) as EditPolygon[]
		});
		return;
	}

	const cos = Math.cos(rotRad);
	const sin = Math.sin(rotRad);

	const lastDx = lastMousePosition.x - cX;
	const lastDy = lastMousePosition.y - cY;
	const lastU = lastDx * cos + lastDy * sin;
	const lastV = -lastDx * sin + lastDy * cos;

	const currDx = click.x - cX;
	const currDy = click.y - cY;
	const currU = currDx * cos + currDy * sin;
	const currV = -currDx * sin + currDy * cos;

	const lastDist = Math.hypot(lastDx, lastDy);
	const currDist = Math.hypot(currDx, currDy);

	let scaleU = 1;
	let scaleV = 1;

	if (shouldKeepAspect) {
		const scaleFactor = lastDist > 1 ? currDist / lastDist : 1;
		const clampedScale = Math.min(Math.max(scaleFactor, 0.7), 1.4);
		scaleU = clampedScale;
		scaleV = clampedScale;
	} else {
		const scaleFactorU = Math.abs(lastU) > 1 ? Math.abs(currU) / Math.abs(lastU) : 1;
		const scaleFactorV = Math.abs(lastV) > 1 ? Math.abs(currV) / Math.abs(lastV) : 1;
		scaleU = Math.min(Math.max(scaleFactorU, 0.7), 1.4);
		scaleV = Math.min(Math.max(scaleFactorV, 0.7), 1.4);
	}

	const currentSpan = Math.max(...points.map(p => Math.hypot(p.x - cX, p.y - cY)));
	if (currentSpan < 8 && (scaleU < 1 || scaleV < 1)) return;

	const newPoints = points.map(p => {
		const dx = p.x - cX;
		const dy = p.y - cY;
		const u = dx * cos + dy * sin;
		const v = -dx * sin + dy * cos;

		const scaledU = u * scaleU;
		const scaledV = v * scaleV;

		return {
			x: Math.round(cX + scaledU * cos - scaledV * sin),
			y: Math.round(cY + scaledU * sin + scaledV * cos)
		};
	});

	const newPolygon = generateMidpoints({
		...polygon,
		points: newPoints
	} as EditPolygon);

	setContent({
		...content,
		polygons: polygons.map((poly, index) => index === selectedMarkup.index ? newPolygon : poly) as EditPolygon[]
	});
}

export function handlePolygonRotate(
	click: PixelCoordinate,
	lastMousePosition: PixelCoordinate,
	content: EditContent,
	setContent: (content: EditContent) => void
) {
	const { polygons } = content;
	const selectedMarkup = content.selectedMarkup!;
	const polygon = polygons[selectedMarkup.index];
	const { points } = polygon;
	
	const cX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
	const cY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
	
	const anglePrev = Math.atan2(lastMousePosition.y - cY, lastMousePosition.x - cX);
	const angleCurr = Math.atan2(click.y - cY, click.x - cX);
	const deltaAngle = angleCurr - anglePrev;
	
	const currentRot = polygon.rotation || 0;
	const deltaRotDegrees = (deltaAngle * 180) / Math.PI;
	const newRot = (currentRot + deltaRotDegrees + 360) % 360;

	if (polygon.isCircle) {
		const { rx, ry } = getCircleEllipseParams(polygon);
		const newPoints = generateCircleEllipsePoints(cX, cY, rx, ry, newRot);

		const newPolygon = generateMidpoints({
			...polygon,
			points: newPoints,
			rotation: newRot
		} as EditPolygon);

		setContent({
			...content,
			polygons: polygons.map((poly, index) => index === selectedMarkup.index ? newPolygon : poly) as EditPolygon[]
		});
		return;
	}
	
	const cos = Math.cos(deltaAngle);
	const sin = Math.sin(deltaAngle);

	const newPoints = points.map(p => {
		const dx = p.x - cX;
		const dy = p.y - cY;
		return {
			x: Math.round(cX + dx * cos - dy * sin),
			y: Math.round(cY + dx * sin + dy * cos)
		};
	});

	const newPolygon = generateMidpoints({
		...polygon,
		points: newPoints,
		rotation: newRot
	} as EditPolygon);
	
	setContent({
		...content,
		polygons: polygons.map((poly, index) => index === selectedMarkup.index ? newPolygon : poly) as EditPolygon[]
	});
}

export function scalePolygon(polygon: EditPolygon, scale: number = 1): EditPolygon {
	if (!polygon) return polygon;
	return {
		...polygon,
		points: (polygon.points || []).map(point => {
			return { x: point.x * scale, y: point.y * scale };
		}),
		midPoints: (polygon.midPoints || []).map(midPoint => {
			return { x: midPoint.x * scale, y: midPoint.y * scale };
		})
	};
}

export function scalePolygons(polygons: EditPolygon[] = [], scale: number = 1): EditPolygon[] {
	if (!polygons || !Array.isArray(polygons)) return [];
	return polygons.map(polygon => scalePolygon(polygon, scale));
}

export function deletePolgonPoint(polygon: EditPolygon, index: number) {
	if (polygon.points.length <= 2) return polygon
	const newPolygon = polygon
	newPolygon.points.splice(index, 1)
	newPolygon.midPoints.splice(index, 1)
	if (index === newPolygon.points.length) index -= 1
	const prevIndex = index === 0 ? newPolygon.points.length - 1 : index - 1
	const nextIndex = (index + 1) % newPolygon.points.length
	newPolygon.midPoints[prevIndex] = calcMidPoint(newPolygon.points[prevIndex], newPolygon.points[index])
	newPolygon.midPoints[index] = calcMidPoint(newPolygon.points[index], newPolygon.points[nextIndex])
	return newPolygon
}

export function generateMidpoints(polygon: EditPolygon): EditPolygon {
	return {
		...polygon,
		midPoints: [
			...polygon.points.map((point, index) => {
				let nextPoint = polygon.points[index + 1]
				if (!nextPoint) nextPoint = polygon.points[0]
				return calcMidPoint(point, nextPoint)
			})
		]
	}
}
